import * as React from "react"
import { FileText, Mail, Image as ImageIcon } from "lucide-react"
import { SiBluesky, SiX, SiYoutube } from "@icons-pack/react-simple-icons"
import { channelColor } from "@/lib/channel-colors"
import { cn } from "@/lib/utils"

// Single source of truth for channel-type → icon, mirroring
// `Channel::CHANNEL_TYPES` in app/models/channel.rb. Brand logos come from
// @icons-pack/react-simple-icons where the channel has a public brand
// identity; LinkedIn uses an inline mark (Simple Icons dropped it
// over a trademark request); YouTube Shorts uses an inline phone-shaped
// mark to convey vertical-video format; lucide stand-ins fill in the rest.
export const CHANNEL_TYPES = [
  "youtube_shorts",
  "youtube_long_form",
  "youtube_post",
  "x",
  "bluesky",
  "linkedin",
  "newsletter",
  "article",
] as const

export type ChannelType = (typeof CHANNEL_TYPES)[number]

type IconComponent = React.ComponentType<{ className?: string }>

// Inline LinkedIn brand mark — neither lucide nor @icons-pack/react-simple-icons
// ships a LinkedIn icon (the latter dropped it after a trademark request).
function LinkedinIcon({ className }: { className?: string }) {
  return (
    <svg
      role="img"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.124 2.062 2.062 0 0 1 0 4.124zM7.119 20.452H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

// Inline YouTube Shorts mark — a vertical rounded-rectangle "phone" body
// with a centered play triangle, hinting at the vertical short-form video
// format that Shorts is built for. Drawn with `currentColor` fills so the
// icon picks up `text-*` utilities and channel tinting like every other
// channel icon.
function YoutubeShortsIcon({ className }: { className?: string }) {
  return (
    <svg
      role="img"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <rect x="6.5" y="1.5" width="11" height="21" rx="2.5" ry="2.5" fill="currentColor" />
      <path d="M10.5 8.5 L10.5 15.5 L16 12 Z" fill="var(--color-page, white)" />
    </svg>
  )
}

const ICONS: Record<ChannelType, IconComponent> = {
  youtube_shorts: YoutubeShortsIcon,
  youtube_long_form: SiYoutube,
  youtube_post: ImageIcon,
  x: SiX,
  bluesky: SiBluesky,
  linkedin: LinkedinIcon,
  newsletter: Mail,
  article: FileText,
}

const LABELS: Record<ChannelType, string> = {
  youtube_shorts: "YouTube Shorts",
  youtube_long_form: "YouTube long-form",
  youtube_post: "YouTube Post",
  x: "X",
  bluesky: "Bluesky",
  linkedin: "LinkedIn",
  newsletter: "Newsletter",
  article: "Article",
}

// Mirrors `Channel::DEFAULT_CHARACTER_LIMITS` on the backend. Used by the
// channel form to seed the character-limit field when the user picks a type
// whose platform has a hard limit. Channels not in this map default to "no
// limit". Keep both maps in sync.
const DEFAULT_CHARACTER_LIMITS: Partial<Record<ChannelType, number>> = {
  x:       280,
  bluesky: 300,
}

export function defaultCharacterLimitFor(
  type: string | null | undefined,
): number | null {
  if (!type) return null
  return DEFAULT_CHARACTER_LIMITS[type as ChannelType] ?? null
}

export function iconForChannelType(
  type: string | null | undefined,
): IconComponent | null {
  if (!type) return null
  return ICONS[type as ChannelType] ?? null
}

export function labelForChannelType(type: string | null | undefined): string {
  if (!type) return ""
  return LABELS[type as ChannelType] ?? type
}

export function ChannelIcon({
  channelType,
  color,
  className,
}: {
  channelType: string | null | undefined
  // Optional channel color name. When provided, the icon is tinted in
  // that channel's mid-tone shade via `channelColor(color).icon` so the
  // brand color reads as itself on a neutral background, instead of
  // looking almost-black like the high-contrast `.text` shade. Callers
  // can still override with a `text-*` utility in `className`.
  //
  // Where the icon sits inside a saturated badge (Pipeline / Workshop /
  // etc.), DON'T pass `color` — let the icon inherit the badge's
  // `c.text` (-900) for high contrast against the -200 cardBg.
  color?: string | null
  className?: string
}) {
  const Icon = iconForChannelType(channelType)
  if (!Icon) return null
  // When a channel color is supplied, append the tint class LAST so it
  // wins over any `text-*` utility the caller passed in `className`
  // (cn() merges via tailwind-merge: last-wins for the same property).
  const tint = color ? channelColor(color).icon : ""
  return <Icon className={cn("shrink-0", className, tint)} />
}
